/**
 * Contact Detail View — Academic Curator
 * Filtered view of a specific contact category with insight cards.
 */

import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Plus, Star, Pencil, Trash2, Calendar, Info, AlertCircle, CheckCircle2 } from 'lucide-react';

import { type EditingContact } from '@/components/ContactSlideOver';

interface ContactDetailPageProps {
  onAddContact: () => void;
  onEditContact: (contact: EditingContact) => void;
}

const MOCK_EMAIL_CONTACTS = [
  { id: '1', value: 'jane.doe@example.com', label: 'Primary Email', rank: 1, status: 'active' as const, period: '01 Jan 2023 - Present', bounced: false, bounceCount: 0 },
  { id: '2', value: 'jane.work@example.com', label: 'Work Email', rank: 2, status: 'active' as const, period: '15 Mar 2023 - Present', bounced: false, bounceCount: 0 },
  { id: '3', value: 'old.email@example.com', label: 'Personal Email', rank: 3, status: 'bounced' as const, period: '10 Jan 2022 - 12 Dec 2022', bounced: true, bounceCount: 3 },
];

export function ContactDetailPage({ onAddContact, onEditContact }: ContactDetailPageProps) {
  const { typeCategory } = useParams<{ typeCategory: string }>();
  const title = typeCategory === 'email' ? 'Email & Digital' : typeCategory === 'address' ? 'Addresses' : 'Contacts';
  const contacts = MOCK_EMAIL_CONTACTS;

  return (
    <div>
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center text-[11px] font-bold tracking-widest text-muted-foreground uppercase mb-6 animate-fade-up">
        <Link to="/profile" className="hover:text-primary transition-colors">Students</Link>
        <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block rotate-12" aria-hidden="true" />
        <Link to="/profile" className="hover:text-primary transition-colors">Jane Doe</Link>
        <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block rotate-12" aria-hidden="true" />
        <span className="text-primary" aria-current="page">{title}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight text-foreground">{title}</h2>
          <p className="text-muted-foreground text-sm font-medium mt-1">Manage student communication preferences and verified digital channels.</p>
        </div>
        <Button className="gap-2 w-full sm:w-auto" onClick={onAddContact} aria-label="Add a new contact">
          <Plus className="w-4 h-4" aria-hidden="true" /> Add Contact
        </Button>
      </div>

      {/* Insight cards — asymmetric */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8 animate-fade-up-delay-1">
        <div className="lg:col-span-8 bg-white p-6 sm:p-8 rounded-2xl shadow-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-accent/50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <Badge variant="primary" className="mb-4 gap-1.5">
              <CheckCircle2 className="w-3 h-3" aria-hidden="true" /> Delivery Performance
            </Badge>
            <h3 className="text-2xl font-bold text-foreground">Primary digital reach is at 94%.</h3>
            <p className="text-muted-foreground mt-2 max-w-md">Consistent delivery to primary and work addresses ensures administrative notices are received within 24 hours.</p>
            <div className="mt-8 flex gap-8">
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Global Reach</p>
                <p className="text-2xl font-black text-foreground">3 <span className="text-surface-highest text-lg">/ 5</span></p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Active Status</p>
                <p className="text-2xl font-black text-primary">High</p>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-4 bg-gradient-to-br from-primary to-indigo-500 p-6 sm:p-8 rounded-2xl shadow-xl shadow-indigo-100 flex flex-col justify-between text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/40 to-transparent" />
          <div className="relative z-10">
            <h3 className="text-xl font-bold leading-tight">Digital Sync Status</h3>
            <p className="text-indigo-100 text-sm mt-2">Active records are synchronized with the Financial Aid and Registrar portals.</p>
          </div>
          <div className="relative z-10 pt-6">
            <button className="w-full py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-lg font-bold text-sm transition-colors" aria-label="Trigger digital sync test">Trigger Sync Test</button>
          </div>
        </div>
      </div>

      {/* Data table */}
      <section aria-label="Contact repository" className="bg-white rounded-2xl shadow-sm overflow-hidden animate-fade-up-delay-2">
        <div className="px-8 py-6 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(210 12% 96%)' }}>
          <h3 className="text-lg font-bold text-foreground">Contact Repository</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-surface-low/50">
              <TableHead className="px-4 sm:px-8 text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Rank</TableHead>
              <TableHead className="px-4 sm:px-8 text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Digital Value</TableHead>
              <TableHead className="hidden sm:table-cell px-8 text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Status</TableHead>
              <TableHead className="hidden md:table-cell px-8 text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Active Period</TableHead>
              <TableHead className="px-4 sm:px-8 text-right text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((c) => (
              <TableRow key={c.id} className="hover:bg-surface-low/50 transition-colors group">
                <TableCell className="px-4 sm:px-8 py-4 sm:py-6">
                  <div className="flex items-center gap-2">
                    {c.rank === 1 ? (
                      <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center text-amber-500" aria-label="Primary contact">
                        <Star className="w-4 h-4" style={{ fill: 'currentColor' }} aria-hidden="true" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-surface-low flex items-center justify-center text-muted-foreground" aria-hidden="true">
                        <span className="text-xs font-bold">{c.rank}</span>
                      </div>
                    )}
                    <span className="text-sm font-bold text-foreground">{c.rank}</span>
                  </div>
                </TableCell>
                <TableCell className="px-4 sm:px-8 py-4 sm:py-6">
                  <div className="flex flex-col">
                    <span className={`text-sm font-bold ${c.bounced ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{c.value}</span>
                    <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">{c.label}</span>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell px-8 py-6">
                  {c.bounced ? (
                    <div className="flex flex-col gap-1">
                      <Badge variant="warning" className="w-fit gap-1">
                        <AlertCircle className="w-3 h-3" aria-hidden="true" /> Bounced
                      </Badge>
                      <span className="text-[9px] font-bold text-amber-500 uppercase tracking-tighter ml-1">Count: {c.bounceCount}</span>
                    </div>
                  ) : (
                    <Badge variant="success" className="gap-1.5">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" aria-hidden="true" /> Active
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="hidden md:table-cell px-8 py-6">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4 text-surface-highest" aria-hidden="true" />
                    <span className={`text-xs font-medium tracking-tight ${c.bounced ? 'line-through' : ''}`}>{c.period}</span>
                  </div>
                </TableCell>
                <TableCell className="px-4 sm:px-8 py-4 sm:py-6 text-right">
                  <div className="flex items-center justify-end gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <button onClick={() => onEditContact({ id: c.id, typeId: 1, contactValue: { val: c.value }, isActive: c.status === 'active', isRecovery: false })} className="p-2 hover:bg-white hover:shadow-md rounded-lg text-muted-foreground hover:text-primary transition-all" aria-label={`Edit ${c.value}`}>
                      <Pencil className="w-4 h-4" aria-hidden="true" />
                    </button>
                    <button className="p-2 hover:bg-white hover:shadow-md rounded-lg text-muted-foreground hover:text-destructive transition-all" aria-label={`Delete ${c.value}`}>
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="px-8 py-4 bg-surface-low/30 text-right" style={{ borderTop: '1px solid hsl(210 12% 96%)' }}>
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Showing {contacts.length} of {contacts.length} digital endpoints</p>
        </div>
      </section>

      {/* Info note */}
      <aside className="bg-accent/50 rounded-2xl p-6 flex items-start gap-4 mt-8 animate-fade-up-delay-3" role="note" style={{ border: '1px solid hsl(243 60% 92%)' }}>
        <div className="bg-white p-2 rounded-lg shadow-sm" aria-hidden="true">
          <Info className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h4 className="text-sm font-bold text-primary">Communication Policy Reminder</h4>
          <p className="text-xs text-primary/70 mt-1 leading-relaxed">Ensure all academic communications are sent to the primary verified address. Bounced emails are automatically suppressed after 5 failed attempts.</p>
        </div>
      </aside>
    </div>
  );
}
